import {
  ActionIcon,
  AppShell,
  Avatar,
  Badge,
  Box,
  Burger,
  Collapse,
  Group,
  Menu,
  ScrollArea,
  Text,
  Tooltip,
  UnstyledButton,
  useMantineColorScheme,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LuChevronDown,
  LuChevronRight,
  LuCode,
  LuLanguages,
  LuLogOut,
  LuPanelLeftClose,
  LuPanelLeftOpen,
  LuSearch,
  LuUser,
} from 'react-icons/lu'
import { TbMoon, TbSun } from 'react-icons/tb'
import { useLogout, useSession } from '../../hooks/useAuth'
import { getLocale, setLocale } from '../../i18n'
import { RouteErrorBoundary } from '../RouteErrorBoundary'
import { CommandPalette } from './CommandPalette'
import paletteClasses from './CommandPalette.module.css'
import { DemoBanner } from './DemoBanner'
import { MarinaBrand } from './MarinaBrand'
import classes from './MarinaLayout.module.css'
import { NotificationBell } from './NotificationBell'
import { filterSidebarByRole, sidebarItems } from './sidebarConfig'

const STORAGE_KEY = 'marina:sidebar:collapsed'
const EXPANDED_STORAGE = 'marina:sidebar:expanded-groups'

export function MarinaLayout() {
  const { t } = useTranslation()
  const { data } = useSession()
  const logout = useLogout()
  const user = data?.user
  const role = user?.role
  const location = useLocation()
  const currentPath = location.pathname

  const [collapsed, setCollapsed] = useState<boolean>(() => localStorage.getItem(STORAGE_KEY) === '1')
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure(false)
  const [paletteOpened, { open: openPalette, close: closePalette }] = useDisclosure(false)
  const isMobile = useMediaQuery('(max-width: 48em)')
  const navigate = useNavigate()
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

  // Global ⌘K / Ctrl+K to open the palette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        openPalette()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [openPalette])

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(EXPANDED_STORAGE) || '{}')
    } catch {
      return {}
    }
  })

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const next = !v
      localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
      return next
    })
  }

  const toggleGroup = (key: string) => {
    setExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      localStorage.setItem(EXPANDED_STORAGE, JSON.stringify(next))
      return next
    })
  }

  const items = filterSidebarByRole(sidebarItems, role)

  const processedItems = items.map((item, idx) => ({
    ...item,
    showSection: !collapsed && !!item.section && (idx === 0 || items[idx - 1]?.section !== item.section),
  }))

  const confirmLogout = () =>
    modals.openConfirmModal({
      title: t('auth.logout_title'),
      children: <Text size="sm">{t('auth.logout_msg')}</Text>,
      labels: { confirm: t('auth.logout'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => logout.mutate(),
    })

  const navbarWidth = collapsed ? 72 : 260

  return (
    <AppShell
      header={{ height: 56, collapsed: !isMobile }}
      navbar={{
        width: navbarWidth,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened },
      }}
      padding="md"
    >
      <AppShell.Header px="md" hiddenFrom="sm">
        <Group h="100%" justify="space-between">
          <Group gap="xs">
            <Burger opened={mobileOpened} onClick={toggleMobile} size="sm" />
            <MarinaBrand />
          </Group>
          <Tooltip label={t('palette.trigger')}>
            <ActionIcon variant="subtle" onClick={openPalette} aria-label={t('palette.trigger')}>
              <LuSearch size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p={0} className={`${classes.sidebar} ${collapsed ? classes.collapsed : ''}`}>
        <AppShell.Section>
          {collapsed ? (
            /* Collapsed: avatar centred in brand bar — click to expand */
            <Tooltip label={t('auth.expand_sidebar')} position="right">
              <UnstyledButton
                onClick={toggleCollapsed}
                className={classes.collapseAvatarBtn}
                aria-label={t('auth.expand_sidebar')}
              >
                <Avatar color="laut" radius="xl" size={44} className={classes.collapseAvatar}>
                  {(user?.name ?? '?').charAt(0).toUpperCase()}
                </Avatar>
                <span className={classes.collapseAvatarDot}>
                  <LuPanelLeftOpen size={12} />
                </span>
              </UnstyledButton>
            </Tooltip>
          ) : (
            /* Expanded: brand on left, avatar chip toggle on right */
            <Group justify="space-between" wrap="nowrap" pr="sm">
              <MarinaBrand collapsed={false} />
              <Tooltip label={t('auth.collapse_sidebar')} position="right">
                <UnstyledButton
                  onClick={toggleCollapsed}
                  className={classes.expandAvatarChip}
                  aria-label={t('auth.collapse_sidebar')}
                >
                  <Avatar color="laut" radius="xl" size={30}>
                    {(user?.name ?? '?').charAt(0).toUpperCase()}
                  </Avatar>
                  <LuPanelLeftClose size={16} className={classes.chipIcon} />
                </UnstyledButton>
              </Tooltip>
            </Group>
          )}
        </AppShell.Section>

        <AppShell.Section px="sm" pt="sm">
          {collapsed ? (
            <Tooltip label={t('palette.trigger')} position="right">
              <button
                type="button"
                onClick={openPalette}
                className={paletteClasses.triggerButtonCollapsed}
                aria-label={t('palette.trigger')}
              >
                <LuSearch size={15} />
              </button>
            </Tooltip>
          ) : (
            <button type="button" onClick={openPalette} className={paletteClasses.triggerButton}>
              <LuSearch size={14} />
              <span className={paletteClasses.triggerLabel}>{t('palette.trigger')}</span>
              <span className={paletteClasses.kbd}>{isMac ? '⌘' : 'Ctrl'}</span>
              <span className={paletteClasses.kbd}>K</span>
            </button>
          )}
        </AppShell.Section>

        <AppShell.Section grow component={ScrollArea} className={classes.navList}>
          {processedItems.map((item) => {
            const Icon = item.icon
            const hasChildren = item.children && item.children.length > 0
            const isGroupActive = hasChildren && item.children!.some((c) => currentPath.startsWith(c.to))
            const isExpanded = expanded[item.key] ?? isGroupActive
            const terminalActive = !hasChildren && item.to && currentPath === item.to

            return (
              <div key={item.key}>
                {item.showSection && (
                  <Text className={classes.navSection}>
                    {t(`nav.section.${item.section}`, { defaultValue: item.section })}
                  </Text>
                )}

                {!hasChildren && item.to ? (
                  collapsed ? (
                    <Tooltip label={t(`nav.${item.key}`, { defaultValue: item.label })} position="right">
                      <Link to={item.to} className={classes.navItem} data-active={terminalActive} onClick={closeMobile}>
                        <Icon size={18} />
                      </Link>
                    </Tooltip>
                  ) : (
                    <Link to={item.to} className={classes.navItem} data-active={terminalActive} onClick={closeMobile}>
                      <Icon size={18} />
                      <span>{t(`nav.${item.key}`, { defaultValue: item.label })}</span>
                    </Link>
                  )
                ) : (
                  <>
                    {collapsed ? (
                      <Tooltip label={t(`nav.${item.key}`, { defaultValue: item.label })} position="right">
                        <Box
                          className={classes.navItem}
                          data-active={isGroupActive}
                          data-group="true"
                          onClick={() => toggleGroup(item.key)}
                        >
                          <Icon size={18} />
                        </Box>
                      </Tooltip>
                    ) : (
                      <Box
                        className={classes.navItem}
                        data-active={isGroupActive}
                        data-group="true"
                        onClick={() => toggleGroup(item.key)}
                      >
                        <Icon size={18} />
                        <span>{t(`nav.${item.key}`, { defaultValue: item.label })}</span>
                        <LuChevronRight size={14} className={classes.chevron} data-expanded={isExpanded} />
                      </Box>
                    )}

                    {!collapsed && (
                      <Collapse in={isExpanded}>
                        {item.children!.map((child) => {
                          const ChildIcon = child.icon
                          const childActive = currentPath === child.to || currentPath.startsWith(`${child.to}/`)
                          return (
                            <Link
                              key={child.to}
                              to={child.to}
                              className={`${classes.navItem} ${classes.navChild}`}
                              data-active={childActive}
                              onClick={closeMobile}
                            >
                              {ChildIcon ? <ChildIcon size={14} /> : <span className={classes.navChildDot} />}
                              <span>{t(`nav.${child.key ?? ''}`, { defaultValue: child.label })}</span>
                            </Link>
                          )
                        })}
                      </Collapse>
                    )}
                  </>
                )}
              </div>
            )
          })}

          {role === 'SUPER_ADMIN' &&
            (collapsed ? (
              <Tooltip label={t('nav.dev_console')} position="right">
                <a href="/dev" className={classes.navItem}>
                  <LuCode size={18} />
                </a>
              </Tooltip>
            ) : (
              <a href="/dev" className={classes.navItem}>
                <LuCode size={18} />
                <span>{t('nav.dev_console')}</span>
              </a>
            ))}
        </AppShell.Section>

        <AppShell.Section className={classes.footer}>
          {collapsed ? (
            <Menu shadow="md" position="right-end" withArrow zIndex={1100}>
              <Menu.Target>
                <UnstyledButton className={classes.footerAvatarBtn}>
                  <Avatar color="laut" radius="xl" size={32}>
                    {(user?.name ?? '?').charAt(0).toUpperCase()}
                  </Avatar>
                </UnstyledButton>
              </Menu.Target>
              <UserMenu onLogout={confirmLogout} />
            </Menu>
          ) : (
            <div className={classes.footerExpanded}>
              <Menu shadow="md" position="top-end" withArrow width={220} zIndex={1100}>
                <Menu.Target>
                  <UnstyledButton className={classes.userCard} aria-label="Akun">
                    <Avatar color="laut" radius="xl" size={36}>
                      {(user?.name ?? '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <div className={classes.userInfo}>
                      <Text size="sm" fw={600} truncate>
                        {user?.name ?? 'User'}
                      </Text>
                      <Badge size="xs" variant="light" color="laut" radius="sm">
                        <RoleLabel role={role} />
                      </Badge>
                    </div>
                    <LuChevronDown size={13} style={{ opacity: 0.4, flexShrink: 0 }} />
                  </UnstyledButton>
                </Menu.Target>
                <UserMenu onLogout={confirmLogout} />
              </Menu>
              <div className={classes.footerActions}>
                <NotificationBell />
                <Tooltip label={t('auth.logout')}>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="md"
                    onClick={confirmLogout}
                    aria-label={t('auth.logout')}
                  >
                    <LuLogOut size={15} />
                  </ActionIcon>
                </Tooltip>
              </div>
            </div>
          )}
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <DemoBanner />
        <RouteErrorBoundary resetKey={currentPath}>
          <Outlet />
        </RouteErrorBoundary>
      </AppShell.Main>

      <CommandPalette
        opened={paletteOpened}
        onClose={closePalette}
        onNavigate={(href) => {
          closeMobile()
          navigate({ to: href })
        }}
      />
    </AppShell>
  )
}

function UserMenu({ onLogout }: { onLogout: () => void }) {
  const { t } = useTranslation()
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()
  const isDark = colorScheme === 'dark'
  // useState so the label updates reactively without full reload
  const [lang, setLang] = useState<'id' | 'en'>(getLocale())

  function switchLanguage() {
    const next = lang === 'id' ? 'en' : 'id'
    setLocale(next)
    setLang(next)
    notifications.show({
      title: next === 'en' ? t('auth.lang_switched_en') : t('auth.lang_switched_id'),
      message: next === 'en' ? t('auth.lang_switched_en_msg') : t('auth.lang_switched_id_msg'),
      color: 'laut',
      autoClose: 4000,
    })
  }

  return (
    <Menu.Dropdown>
      <Menu.Item leftSection={<LuUser size={14} />} component="a" href="/profile">
        {t('auth.profile')}
      </Menu.Item>
      <Menu.Item
        leftSection={isDark ? <TbSun size={14} /> : <TbMoon size={14} />}
        closeMenuOnClick={false}
        onClick={() => toggleColorScheme()}
      >
        {isDark ? t('auth.light_mode') : t('auth.dark_mode')}
      </Menu.Item>
      <Menu.Item leftSection={<LuLanguages size={14} />} closeMenuOnClick={false} onClick={switchLanguage}>
        {lang === 'id' ? '🇬🇧 Switch to English' : '🇮🇩 Ganti ke Bahasa Indonesia'}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item color="red" leftSection={<LuLogOut size={14} />} onClick={onLogout}>
        {t('auth.logout')}
      </Menu.Item>
    </Menu.Dropdown>
  )
}

function RoleLabel({ role }: { role: string | undefined }) {
  const { t } = useTranslation()
  return <>{t(`auth.role.${role ?? 'default'}`, { defaultValue: role ?? 'Pengguna' })}</>
}
